// Browser auto-translation (Chrome Translate, Edge Translate, Google Translate
// extension) wraps text nodes in injected <font> elements. React keeps an internal
// pointer to the original parent of each node, so when it later tries to remove or
// move a node whose DOM parent has been silently changed by the translator, the
// browser throws "NotFoundError: Failed to execute 'removeChild' on 'Node'" and
// the entire React root unmounts (blank screen).
//
// We patch Node.removeChild and Node.insertBefore so that when React asks to remove
// or move a node whose actual parent has changed, we redirect to the real parent
// (or no-op) instead of throwing. React keeps reconciling, the page keeps working,
// and visitors using browser translation no longer see a blank screen.
//
// Same approach used in production by major React apps. Tracked upstream in
// facebook/react#11538.
const PATCH_TAG =
  "[translatePatch] suppressed Node mutation that would have crashed React; likely caused by browser auto-translate or a DOM-mutating extension";

// Use pathname only — avoid leaking auth tokens that ride in query strings
// (e.g. /touchless?tkn=...).
function currentPath(): string {
  try {
    return typeof window !== "undefined" ? window.location.pathname : "unknown";
  } catch {
    return "unknown";
  }
}

export function installTranslatePatch(): void {
  if (typeof Node === "undefined" || !Node.prototype) return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      console.info(
        `${PATCH_TAG} (removeChild: child reparented) page=${currentPath()}`,
      );
      if (child.parentNode) {
        return originalRemoveChild.call(child.parentNode, child) as T;
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      console.info(
        `${PATCH_TAG} (insertBefore: reference reparented) page=${currentPath()}`,
      );
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
